// Console Logger Utility for Remote Debugging
interface LogEntry {
  timestamp: number;
  level: 'log' | 'info' | 'warn' | 'error';
  message: string;
  data?: any;
  stack?: string;
}

class ConsoleLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;
  private originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error
  };

  constructor() {
    // Only enable console interception in development mode
    if (import.meta.env.PROD) {
      return; // Don't intercept in production
    }
    this.interceptConsole();
    this.setupErrorHandlers();
  }

  private interceptConsole() {
    // Override console methods
    console.log = (...args) => {
      this.addLog('log', args);
      this.originalConsole.log(...args);
    };

    console.info = (...args) => {
      this.addLog('info', args);
      this.originalConsole.info(...args);
    };

    console.warn = (...args) => {
      this.addLog('warn', args);
      this.originalConsole.warn(...args);
    };

    console.error = (...args) => {
      this.addLog('error', args);
      this.originalConsole.error(...args);
    };
  }

  private setupErrorHandlers() {
    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.addLog('error', [event.message], event.error?.stack);
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.addLog('error', ['Unhandled promise rejection:', event.reason]);
    });
  }

  private addLog(level: LogEntry['level'], args: any[], stack?: string) {
    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      message: args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' '),
      data: args.length > 1 ? args : undefined,
      stack
    };

    this.logs.push(entry);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  // Get all logs
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  // Get logs by level
  getLogsByLevel(level: LogEntry['level']): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  // Get logs from the last N minutes
  getRecentLogs(minutes: number): LogEntry[] {
    const cutoff = Date.now() - (minutes * 60 * 1000);
    return this.logs.filter(log => log.timestamp > cutoff);
  }

  // Clear all logs
  clearLogs() {
    this.logs = [];
  }

  // Export logs as JSON
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Copy logs to clipboard
  async copyLogsToClipboard() {
    const logsText = this.logs.map(log => {
      const time = new Date(log.timestamp).toLocaleTimeString();
      return `[${time}] [${log.level.toUpperCase()}] ${log.message}${log.stack ? '\n' + log.stack : ''}`;
    }).join('\n\n');

    try {
      await navigator.clipboard.writeText(logsText);
      this.originalConsole.log('Logs copied to clipboard!');
      return true;
    } catch (err) {
      this.originalConsole.error('Failed to copy logs:', err);
      return false;
    }
  }

  // Download logs as file
  downloadLogs() {
    const logsText = this.exportLogs();
    const blob = new Blob([logsText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `console-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// Create singleton instance
export const consoleLogger = new ConsoleLogger();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).consoleLogger = consoleLogger;
}

// Helper functions for quick access
export const getConsoleLogs = () => consoleLogger.getLogs();
export const getErrorLogs = () => consoleLogger.getLogsByLevel('error');
export const copyLogsToClipboard = () => consoleLogger.copyLogsToClipboard();
export const downloadConsoleLogs = () => consoleLogger.downloadLogs();